import { useState, useMemo, useRef } from "react";
import { timeSlots } from "../../../mocks/bookingData";
import type { BookingState } from "../page";

interface BookingCalendarProps {
  booking: BookingState;
  onUpdate: (updates: Partial<BookingState>) => void;
  error?: string;
  photos: { file: File; preview: string }[];
  onAddPhotos: (files: FileList | null) => void;
  onRemovePhoto: (idx: number) => void;
}

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

function getDaysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}
function getFirstDayOfMonth(year: number, month: number) {
  return new Date(year, month, 1).getDay();
}

export default function BookingCalendar({ booking, onUpdate, error, photos, onAddPhotos, onRemovePhoto }: BookingCalendarProps) {
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstDay = getFirstDayOfMonth(viewYear, viewMonth);

  const isAvailable = (day: number) => {
    const date = new Date(viewYear, viewMonth, day);
    date.setHours(0, 0, 0, 0);
    const dayOfWeek = date.getDay();
    const diffDays = (date.getTime() - today.getTime()) / (1000 * 60 * 60 * 24);
    if (dayOfWeek === 0) return false;
    if (diffDays < 0 || diffDays > 30) return false;
    return true;
  };

  const isSameDay = (day: number) => {
    if (!booking.date) return false;
    const [y, m, d] = booking.date.split("-").map(Number);
    return y === viewYear && (m - 1) === viewMonth && d === day;
  };

  const isToday = (day: number) =>
    today.getFullYear() === viewYear && today.getMonth() === viewMonth && today.getDate() === day;

  const selectDate = (day: number) => {
    if (!isAvailable(day)) return;
    const mm = String(viewMonth + 1).padStart(2, "0");
    const dd = String(day).padStart(2, "0");
    onUpdate({ date: `${viewYear}-${mm}-${dd}`, timeSlot: "", timeSlotLabel: "" });
  };

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear((y) => y - 1); }
    else setViewMonth((m) => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear((y) => y + 1); }
    else setViewMonth((m) => m + 1);
  };

  const isPrevDisabled = viewYear === today.getFullYear() && viewMonth === today.getMonth();

  const cells: (number | null)[] = [
    ...Array(firstDay).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
      {/* ── Left: Calendar ── */}
      <div>
        <div className="bg-white rounded-2xl cartoon-card overflow-hidden">
          <div className="bg-cl-dark px-6 py-4 flex items-center justify-between">
            <button onPointerUp={(e) => { e.preventDefault(); if (!isPrevDisabled) prevMonth(); }} disabled={isPrevDisabled}
              style={{ touchAction: "manipulation", WebkitTapHighlightColor: "transparent" }}
              className={`w-11 h-11 flex items-center justify-center rounded-xl border-2 text-white transition-all cursor-pointer ${isPrevDisabled ? "border-white/20 opacity-30" : "border-white/40 active:bg-white/20"}`}>
              <i className="ri-arrow-left-s-line text-lg"></i>
            </button>
            <h3 className="text-white font-black text-base">{MONTHS[viewMonth]} {viewYear}</h3>
            <button onPointerUp={(e) => { e.preventDefault(); nextMonth(); }}
              style={{ touchAction: "manipulation", WebkitTapHighlightColor: "transparent" }}
              className="w-11 h-11 flex items-center justify-center rounded-xl border-2 border-white/40 text-white active:bg-white/20 transition-all cursor-pointer">
              <i className="ri-arrow-right-s-line text-lg"></i>
            </button>
          </div>

          <div className="grid grid-cols-7 border-b-2 border-transparent">
            {DAYS.map((d) => (
              <div key={d} className={`py-2 text-center text-xs font-black uppercase tracking-wide ${d === "Sun" ? "text-gray-300" : "text-gray-500"}`}>{d}</div>
            ))}
          </div>

          <div className="grid grid-cols-7 p-2 gap-1">
            {cells.map((day, idx) => {
              if (day === null) return <div key={`e-${idx}`}></div>;
              const available = isAvailable(day);
              const selected = isSameDay(day);
              const todayDay = isToday(day);
              return (
                <button
                  key={day}
                  onClick={() => selectDate(day)}
                  disabled={!available}
                  style={{ touchAction: "manipulation" }}
                  className={`h-11 w-full flex items-center justify-center rounded-xl text-sm font-bold transition-all
                    ${selected ? "bg-cl-green text-white border-2 border-[#4a8c0a]" : ""}
                    ${todayDay && !selected ? "border-2 border-cl-dark text-cl-dark font-black" : ""}
                    ${available && !selected ? "active:bg-cl-lime active:text-cl-dark cursor-pointer hover:bg-cl-lime/60" : ""}
                    ${!available ? "text-gray-300 cursor-default" : ""}
                  `}
                >
                  {day}
                </button>
              );
            })}
          </div>

          <div className="px-5 py-3 border-t-2 border-transparent flex items-center gap-5 flex-wrap">
            <div className="flex items-center gap-1.5"><div className="w-3.5 h-3.5 rounded-md bg-cl-green border border-[#4a8c0a]"></div><span className="text-xs text-gray-500 font-semibold">Selected</span></div>
            <div className="flex items-center gap-1.5"><div className="w-3.5 h-3.5 rounded-md bg-white border-2 border-cl-dark"></div><span className="text-xs text-gray-500 font-semibold">Today</span></div>
            <div className="flex items-center gap-1.5"><div className="w-3.5 h-3.5 rounded-md bg-gray-100"></div><span className="text-xs text-gray-500 font-semibold">Unavailable</span></div>
          </div>
        </div>
      </div>

      {/* ── Right: Upload + Time Slots ── */}
      <div className="flex flex-col gap-4">
        {/* Upload */}
        <div className="bg-white rounded-2xl cartoon-card overflow-hidden">
          <div className="px-4 py-2.5 border-b-2 border-transparent flex items-center gap-2">
            <span className="w-6 h-6 flex items-center justify-center rounded-md bg-cl-lime border-2 border-cl-green text-cl-green flex-shrink-0">
              <i className="ri-camera-line text-xs"></i>
            </span>
            <div>
              <h4 className="font-black text-cl-dark text-xs">Photos of your collection</h4>
              <p className="text-xs text-gray-400 font-semibold">Optional but helps our team prepare</p>
            </div>
          </div>
          <div className="p-3">
            <div
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => { e.preventDefault(); setDragging(false); onAddPhotos(e.dataTransfer.files); }}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-xl px-4 py-4 flex flex-col items-center gap-1.5 cursor-pointer transition-all ${dragging ? "border-cl-green bg-[#f0fce4]" : "border-transparent hover:border-cl-green hover:bg-[#f8fef4]"}`}
            >
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-cl-lime border-2 border-cl-green text-cl-green">
                <i className="ri-upload-cloud-2-line text-base"></i>
              </span>
              <p className="text-xs font-black text-cl-dark">Click to upload or drag &amp; drop</p>
              <p className="text-xs text-gray-400 font-semibold">JPG, PNG, WEBP &nbsp;·&nbsp; up to 8 photos</p>
              <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => onAddPhotos(e.target.files)} />
            </div>

            {photos.length > 0 && (
              <div className="mt-2.5 grid grid-cols-6 gap-1.5">
                {photos.map((p, idx) => (
                  <div key={idx} className="relative group">
                    <div className="w-full aspect-square rounded-lg overflow-hidden border-2 border-transparent">
                      <img src={p.preview} alt={`upload-${idx}`} className="w-full h-full object-cover" />
                    </div>
                    <button onClick={() => onRemovePhoto(idx)}
                      className="absolute -top-1.5 -right-1.5 w-5 h-5 flex items-center justify-center rounded-full bg-red-500 border-2 border-white text-white opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                      <i className="ri-close-line text-xs"></i>
                    </button>
                  </div>
                ))}
                {photos.length < 8 && (
                  <button onClick={() => fileInputRef.current?.click()}
                    className="w-full aspect-square rounded-lg border-2 border-dashed border-transparent hover:border-cl-green flex items-center justify-center text-gray-400 hover:text-cl-green transition-colors cursor-pointer">
                    <i className="ri-add-line text-lg"></i>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Time slots */}
        {booking.date ? (
          <div className="bg-white rounded-2xl cartoon-card p-3">
            <p className="text-sm font-black text-cl-dark mb-2">
              Available slots for{" "}
              <span className="text-cl-green">
                {new Date(booking.date + "T00:00:00").toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}
              </span>
            </p>
            <div className="grid grid-cols-2 gap-2">
              {timeSlots.map((slot) => {
                const selected = booking.timeSlot === slot.id;
                return (
                  <button
                    key={slot.id}
                    onPointerUp={(e) => { e.preventDefault(); onUpdate({ timeSlot: slot.id, timeSlotLabel: `${slot.label} (${slot.sublabel})` }); }}
                    style={{ touchAction: "manipulation", WebkitTapHighlightColor: "transparent" }}
                    className={`flex items-center gap-2 p-3 rounded-xl border-2 transition-all cursor-pointer text-left active:scale-95 ${selected ? "border-cl-green bg-[#f0fce4]" : "border-gray-200 hover:border-cl-green"}`}
                  >
                    <span className={`w-9 h-9 flex items-center justify-center rounded-lg border-2 text-sm flex-shrink-0 ${selected ? "bg-cl-green border-[#4a8c0a] text-white" : "bg-cl-lime border-cl-green text-cl-green"}`}>
                      <i className={slot.icon}></i>
                    </span>
                    <div className="min-w-0">
                      <p className="text-xs font-black text-cl-dark leading-tight">{slot.label}</p>
                      <p className="text-xs text-gray-500 font-semibold leading-tight">{slot.sublabel}</p>
                      <p className={`text-xs font-black leading-tight ${slot.price === 0 ? "text-cl-green" : "text-orange-500"}`}>
                        {slot.price === 0 ? "Free" : `+£${slot.price.toFixed(2)}`}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl cartoon-card p-3 flex flex-col items-center justify-center gap-1.5 text-center py-5">
            <span className="w-8 h-8 flex items-center justify-center rounded-full bg-cl-tint border-2 border-transparent text-cl-green">
              <i className="ri-calendar-line text-base"></i>
            </span>
            <p className="text-sm font-black text-cl-dark">Pick a date first</p>
            <p className="text-xs text-gray-400 font-semibold">Time slots will appear here once you select a date on the calendar.</p>
          </div>
        )}

        {error && (
          <div className="flex items-center gap-2 text-sm font-bold text-red-500 bg-red-50 border-2 border-red-200 rounded-xl px-4 py-3">
            <i className="ri-error-warning-line"></i>{error}
          </div>
        )}
      </div>
    </div>
  );
}